// IndexedDB storage for the persisted React Query cache (`PersistQueryClientProvider` in the web app).
//
// User isolation: the stored value is tagged with the id of the user it belongs to (`ownerId`).
// - Writes are tagged with the current user; with no signed-in user nothing is stored (the entry is deleted).
// - A read returns the cache only when its owner is the current user; otherwise the entry is deleted and nothing
//   is restored. So one user's cached data is never shown to another user on a shared device — even if logout
//   was skipped (e.g. the app was closed, storage edited, or a different account signed in).
// - `logout()` additionally deletes the entry (`clearPersistedQueryCache`).
//
// Kept separate from the outbox database (`sportschool-offline`), so clearing the query cache never touches
// unsent coach changes.
import { createStore, del, get, set, type UseStore } from 'idb-keyval';

export const QUERY_CACHE_DB_NAME = 'sportschool-query-cache';
export const QUERY_CACHE_STORE_NAME = 'cache';
/** Key of the persisted client inside the store (also the persister `key`). */
export const QUERY_CACHE_KEY = 'react-query';

interface StoredQueryCache {
  ownerId: string;
  value: string;
}

let store: UseStore | null = null;
const getStore = () => (store ??= createStore(QUERY_CACHE_DB_NAME, QUERY_CACHE_STORE_NAME));

/** Async storage (`createAsyncStoragePersister({ storage })`) whose entries are bound to the current user. */
export interface PersistedQueryStorage {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
}

/**
 * @param getOwnerId resolves the current user's id (`null` = signed out); evaluated on every read and write.
 */
export function createPersistedQueryStorage(getOwnerId: () => Promise<string | null>): PersistedQueryStorage {
  return {
    async getItem(key) {
      try {
        const [stored, ownerId] = await Promise.all([get<StoredQueryCache>(key, getStore()), getOwnerId()]);
        if (!stored) return null;
        if (!ownerId || stored.ownerId !== ownerId || typeof stored.value !== 'string') {
          await del(key, getStore());
          return null;
        }
        return stored.value;
      } catch {
        return null;
      }
    },
    async setItem(key, value) {
      try {
        const ownerId = await getOwnerId();
        if (!ownerId) {
          await del(key, getStore());
          return;
        }
        await set(key, { ownerId, value } satisfies StoredQueryCache, getStore());
      } catch {
        // Quota / private mode: the app still works, just without an offline cache.
      }
    },
    async removeItem(key) {
      try {
        await del(key, getStore());
      } catch {
        // ignore
      }
    },
  };
}

/** Deletes the persisted query cache (logout, user change). Never touches the outbox. */
export async function clearPersistedQueryCache(): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  try {
    await del(QUERY_CACHE_KEY, getStore());
  } catch {
    // ignore
  }
}
